import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { 
  Palette, ArrowLeft, Check, X, CreditCard, Zap, Crown, 
  Loader2, Sparkles, Shield, Star, HelpCircle, ChevronDown, ChevronUp,
  Info
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tooltip component
const Tooltip = ({ children, text }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-block">
      <div 
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
        </div>
      )}
    </div>
  );
};

const Pricing = () => {
  const { user, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [purchasing, setPurchasing] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  // Auto-subscribe if user lands with plan query params (from pending plan redirect)
  useEffect(() => {
    const planId = searchParams.get('plan');
    const annual = searchParams.get('annual') === 'true';
    
    if (planId && user && ['basic', 'plus', 'pro'].includes(planId)) {
      // Clear the URL params
      navigate('/pricing', { replace: true });
      
      // Set annual state and trigger subscription
      setIsAnnual(annual);
      
      // Small delay to ensure state is set
      setTimeout(() => {
        handleSubscribeDirectly(planId, annual);
      }, 100);
    }
  }, [user, searchParams, navigate]);

  // Direct subscribe without checks (used for auto-redirect flow)
  const handleSubscribeDirectly = async (planId, annual) => {
    setPurchasing(planId);
    try {
      const response = await axios.post(
        `${API}/subscribe`,
        {
          plan: planId,
          is_annual: annual,
          origin_url: window.location.origin,
        },
        { withCredentials: true }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Subscribe error:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setPurchasing(null);
    }
  };

  const tooltips = {
    commercial: "Sell your coloring pages on Etsy, Amazon KDP, Teachers Pay Teachers, or include them in products. Perfect for creators and businesses.",
    batch: "Generate multiple variations of the same prompt at once. Plus: 3 variations, Pro: 5 variations. Save time creating themed sets!",
    storage: "Access and re-download all your creations anytime. Free/Basic users: 7-day access only.",
    priority: "Get help within 24 hours. Regular support: 48-72 hour response time.",
    photoToColoring: "Upload any photo and convert it to a beautiful line art coloring page. Perfect for personalizing coloring books!",
    aiGeneration: "Create stunning coloring pages from simple text descriptions. Our AI understands what makes great coloring pages.",
    freeTools: "Access our free tools: Name Generator, Bubble Letters, Quote Pages - no signup required!"
  };

  const plans = [
    {
      id: "free",
      name: "Free",
      priceMonthly: 0,
      priceAnnual: 0,
      monthlyEquivalent: 0,
      pagesPerMonth: 10,
      pagesPerDay: 3,
      tagline: "Get started creating coloring pages",
      features: [
        { text: "AI coloring page generation", included: true, highlight: true },
        { text: "10 pages per month", included: true },
        { text: "High-quality PDFs (300 DPI)", included: true },
        { text: "Access to Free Tools", included: true, tooltip: "freeTools" },
        { text: "Personal use only", included: true },
        { text: "Files stored 7 days", included: true },
        { text: "Photo to Coloring Page", included: false, tooltip: "photoToColoring" },
        { text: "Commercial license", included: false, tooltip: "commercial" },
        { text: "Batch generation", included: false, tooltip: "batch" },
      ],
      icon: Sparkles,
      color: "slate",
      buttonText: "Start Free"
    },
    {
      id: "basic",
      name: "Basic",
      priceMonthly: 4.99,
      priceAnnual: 49.99,
      monthlyEquivalent: 4.17,
      annualSavings: 10,
      pagesPerMonth: 50,
      pagesPerDay: 5,
      tagline: "Perfect for hobbyists and parents",
      features: [
        { text: "AI coloring page generation", included: true, highlight: true },
        { text: "50 pages per month", included: true },
        { text: "High-quality PDFs (300 DPI)", included: true },
        { text: "Photo to Coloring (3/month)", included: true, highlight: true, tooltip: "photoToColoring" },
        { text: "Create pages with names & text", included: true },
        { text: "Access to Free Tools", included: true },
        { text: "Email support", included: true },
        { text: "Files stored 7 days", included: true },
        { text: "Commercial license", included: false, tooltip: "commercial" },
        { text: "Batch generation", included: false, tooltip: "batch" },
      ],
      icon: CreditCard,
      color: "blue"
    },
    {
      id: "plus",
      name: "Plus",
      priceMonthly: 9.99,
      priceAnnual: 99.99,
      monthlyEquivalent: 8.33,
      annualSavings: 20,
      pagesPerMonth: 150,
      pagesPerDay: 15,
      popular: true,
      tagline: "Best for creators selling on Etsy & KDP",
      features: [
        { text: "AI coloring page generation", included: true, highlight: true },
        { text: "150 pages per month", included: true },
        { text: "Photo to Coloring (15/month)", included: true, highlight: true, tooltip: "photoToColoring" },
        { text: "Commercial license included", included: true, tooltip: "commercial" },
        { text: "Batch generation (3 at once)", included: true, tooltip: "batch" },
        { text: "Create pages with names & text", included: true },
        { text: "Permanent file storage", included: true, tooltip: "storage" },
        { text: "Re-download anytime", included: true },
        { text: "Email support", included: true },
      ],
      icon: Zap,
      color: "violet"
    },
    {
      id: "pro",
      name: "Pro",
      priceMonthly: 14.99,
      priceAnnual: 149.99,
      monthlyEquivalent: 12.49,
      annualSavings: 30,
      pagesPerMonth: 300,
      pagesPerDay: 30,
      tagline: "For businesses & power users",
      features: [
        { text: "AI coloring page generation", included: true, highlight: true },
        { text: "300 pages per month", included: true },
        { text: "Photo to Coloring (50/month)", included: true, highlight: true, tooltip: "photoToColoring" },
        { text: "Commercial license included", included: true, tooltip: "commercial" },
        { text: "Batch generation (5 at once)", included: true, tooltip: "batch" },
        { text: "Create pages with names & text", included: true },
        { text: "Permanent file storage", included: true, tooltip: "storage" },
        { text: "Priority support (24hr)", included: true, tooltip: "priority" },
        { text: "Bulk download all pages", included: true },
        { text: "Early access to new features", included: true },
      ],
      icon: Crown,
      color: "amber"
    }
  ];

  const faqs = [
    {
      q: "Can I upgrade or downgrade anytime?",
      a: "Yes! Upgrades take effect immediately. Downgrades take effect at the next billing cycle."
    },
    {
      q: "What happens to unused pages?",
      a: "Unused pages don't roll over. Your limit resets monthly."
    },
    {
      q: "Can I cancel anytime?",
      a: "Yes, cancel anytime. No refunds for partial months."
    },
    {
      q: "What's the difference between plans?",
      a: "All plans have the same quality. You pay for more pages and extra features like commercial licenses."
    },
    {
      q: "Do you offer refunds?",
      a: "Yes, 7-day money-back guarantee on first purchase."
    },
    {
      q: 'What does "commercial license" mean?',
      a: "You can sell the coloring pages or use them in commercial products."
    },
    {
      q: "How long are files stored?",
      a: "Free/Basic: 7 days. Plus/Pro: Forever."
    },
    {
      q: "Can I download pages again later?",
      a: "Plus/Pro: Yes, anytime. Free/Basic: Within 7 days only."
    }
  ];

  const examplePrompts = [
    { emoji: "🦄", text: "Unicorn in enchanted forest" },
    { emoji: "🦖", text: "Friendly dinosaurs at a party" },
    { emoji: "🐉", text: "Cute dragon with fairy friends" },
    { emoji: "🌊", text: "Underwater mermaid castle" },
    { emoji: "🚗", text: "Race cars and trucks" },
    { emoji: "🌸", text: "Mandala with butterflies" },
    { emoji: "🎂", text: "Birthday cake and balloons" },
    { emoji: "🐱", text: "Kittens playing with yarn" },
  ];

  const handleSubscribe = async (planId) => {
    if (planId === "free") {
      // If user is already logged in, just go to dashboard or generator
      if (user) {
        toast.success("You're already on the Free plan!");
        navigate("/generate");
        return;
      }
      navigate("/signup");
      return;
    }
    
    if (!user) {
      // Store the intended plan so we can redirect after login
      localStorage.setItem('colorwonder_pending_plan', JSON.stringify({
        planId: planId,
        isAnnual: isAnnual,
        timestamp: new Date().toISOString()
      }));
      toast.info("Please sign in to subscribe to this plan");
      navigate("/login");
      return;
    }

    setPurchasing(planId);

    try {
      const response = await axios.post(
        `${API}/subscribe`,
        {
          plan: planId,
          is_annual: isAnnual,
          origin_url: window.location.origin,
        },
        { withCredentials: true }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Subscribe error:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setPurchasing(null);
    }
  };

  const getPrice = (plan) => {
    if (plan.id === "free") return "$0";
    if (isAnnual) {
      return `$${plan.monthlyEquivalent.toFixed(2)}`;
    }
    return `$${plan.priceMonthly.toFixed(2)}`;
  };

  const getBilledText = (plan) => {
    if (plan.id === "free") return "forever";
    if (isAnnual) {
      return `billed $${plan.priceAnnual}/year`;
    }
    return "/month";
  };

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 text-slate-600 hover:text-slate-900" data-testid="pricing-back">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </Link>

            <Link to="/" className="flex items-center gap-2" data-testid="pricing-logo">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            {user ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-violet-50 rounded-full">
                <span className="text-sm text-slate-600">Current plan:</span>
                <span className="font-semibold text-violet-700 capitalize">{user.subscription_tier || "free"}</span>
              </div>
            ) : (
              <Button
                onClick={() => navigate("/login")}
                className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold btn-lift"
                data-testid="pricing-login"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Choose the plan that fits your creative needs. Upgrade or downgrade anytime.
          </p>
        </div>

        {/* What's Included in All Plans */}
        <div className="bg-gradient-to-r from-violet-50 to-pink-50 rounded-3xl p-8 border border-violet-100 mb-12">
          <h2 className="text-xl font-bold text-slate-800 mb-6 text-center" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Every Plan Includes
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              "AI-powered coloring page generation",
              "Print-ready 300 DPI quality",
              "Instant generation (under 15 sec)",
              "Unlimited creative themes",
              "Clean black & white line art",
              "No watermarks ever"
            ].map((feature, index) => (
              <div key={index} className="flex items-start gap-2 text-sm">
                <Check className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <span className={`font-medium ${!isAnnual ? 'text-slate-900' : 'text-slate-500'}`}>Monthly</span>
          <Switch
            checked={isAnnual}
            onCheckedChange={setIsAnnual}
            className="data-[state=checked]:bg-violet-600"
            data-testid="billing-toggle"
          />
          <span className={`font-medium ${isAnnual ? 'text-slate-900' : 'text-slate-500'}`}>
            Annual
            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Save 17%!</span>
          </span>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {plans.map((plan) => {
            const isCurrentPlan = user?.subscription_tier === plan.id || (!user?.subscription_tier && plan.id === "free");
            const Icon = plan.icon;
            
            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-3xl p-6 border-2 transition-all ${
                  plan.popular 
                    ? 'border-violet-500 shadow-xl shadow-violet-100' 
                    : 'border-slate-100 hover:border-violet-200'
                }`}
                data-testid={`plan-${plan.id}`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3 fill-white" />
                    BEST VALUE
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-6">
                  <div className={`w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center ${
                    plan.popular ? 'bg-gradient-to-br from-violet-600 to-indigo-600' : 'bg-slate-100'
                  }`}>
                    <Icon className={`w-7 h-7 ${plan.popular ? 'text-white' : 'text-slate-600'}`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {plan.name}
                  </h3>
                  {plan.tagline && (
                    <p className="text-xs text-slate-500 mt-1">{plan.tagline}</p>
                  )}
                </div>

                {/* Price */}
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-slate-800">{getPrice(plan)}</span>
                    {plan.id !== "free" && <span className="text-slate-500">/mo</span>}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{getBilledText(plan)}</p>
                  {isAnnual && plan.annualSavings && (
                    <p className="text-sm text-green-600 font-medium mt-1">Save ${plan.annualSavings}/year</p>
                  )}
                </div>

                {/* Pages Info */}
                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-center">
                  <div className="text-2xl font-bold text-violet-600">{plan.pagesPerMonth}</div>
                  <div className="text-sm text-slate-600">pages per month</div>
                  <div className="text-xs text-slate-500 mt-1">{plan.pagesPerDay} pages/day max</div>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      {feature.included ? (
                        <Check className={`w-5 h-5 flex-shrink-0 ${feature.highlight ? 'text-violet-500' : 'text-green-500'}`} />
                      ) : (
                        <X className="w-5 h-5 text-slate-300 flex-shrink-0" />
                      )}
                      <span className={`text-sm ${
                        feature.included 
                          ? feature.highlight 
                            ? 'text-violet-700 font-medium' 
                            : 'text-slate-700' 
                          : 'text-slate-400'
                      }`}>
                        {feature.text}
                      </span>
                      {feature.tooltip && (
                        <Tooltip text={tooltips[feature.tooltip]}>
                          <Info className="w-4 h-4 text-slate-400 hover:text-violet-500" />
                        </Tooltip>
                      )}
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={purchasing !== null || (isCurrentPlan && plan.id !== "free")}
                  className={`w-full h-12 rounded-full font-semibold ${
                    plan.popular
                      ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:shadow-lg'
                      : plan.id === "free"
                      ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                  data-testid={`subscribe-${plan.id}`}
                >
                  {purchasing === plan.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isCurrentPlan && plan.id !== "free" ? (
                    "Current Plan"
                  ) : isCurrentPlan && plan.id === "free" ? (
                    "Your Current Plan"
                  ) : plan.id === "free" ? (
                    user ? "Switch to Free" : "Start Free"
                  ) : (
                    `Get ${plan.name}`
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Feature Comparison Table */}
        <div className="bg-white rounded-3xl p-8 border border-slate-100 mb-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Feature Comparison
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-4 px-4 font-semibold text-slate-700">Feature</th>
                  <th className="text-center py-4 px-4 font-semibold text-slate-700">Free</th>
                  <th className="text-center py-4 px-4 font-semibold text-slate-700">Basic</th>
                  <th className="text-center py-4 px-4 font-semibold text-violet-700 bg-violet-50 rounded-t-xl">Plus</th>
                  <th className="text-center py-4 px-4 font-semibold text-slate-700">Pro</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-600">Pages per month</td>
                  <td className="py-4 px-4 text-center font-medium">10</td>
                  <td className="py-4 px-4 text-center font-medium">50</td>
                  <td className="py-4 px-4 text-center font-medium bg-violet-50">150</td>
                  <td className="py-4 px-4 text-center font-medium">300</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-600">Pages per day</td>
                  <td className="py-4 px-4 text-center">3</td>
                  <td className="py-4 px-4 text-center">5</td>
                  <td className="py-4 px-4 text-center bg-violet-50">15</td>
                  <td className="py-4 px-4 text-center">30</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-600">High-quality PDFs</td>
                  <td className="py-4 px-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="py-4 px-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="py-4 px-4 text-center bg-violet-50"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="py-4 px-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-600">Personal use</td>
                  <td className="py-4 px-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="py-4 px-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="py-4 px-4 text-center bg-violet-50"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="py-4 px-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-600">Commercial license</td>
                  <td className="py-4 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-4 text-center bg-violet-50"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="py-4 px-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-600">Batch generation</td>
                  <td className="py-4 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-4 text-center bg-violet-50 font-medium text-violet-600">3x</td>
                  <td className="py-4 px-4 text-center font-medium text-violet-600">5x</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-600">File storage</td>
                  <td className="py-4 px-4 text-center text-slate-500">7 days</td>
                  <td className="py-4 px-4 text-center text-slate-500">7 days</td>
                  <td className="py-4 px-4 text-center bg-violet-50 font-medium text-violet-600">∞</td>
                  <td className="py-4 px-4 text-center font-medium text-violet-600">∞</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-600">Priority support</td>
                  <td className="py-4 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-4 text-center bg-violet-50"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-slate-600">Bulk download</td>
                  <td className="py-4 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-4 text-center bg-violet-50 rounded-b-xl"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                  <td className="py-4 px-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Example Prompts */}
        <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-3xl p-8 border border-violet-100 mb-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Try These Popular Prompts
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {examplePrompts.map((prompt, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-4 text-center border border-slate-100 hover:border-violet-200 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate("/generate")}
              >
                <span className="text-2xl block mb-2">{prompt.emoji}</span>
                <span className="text-sm text-slate-600">"{prompt.text}"</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Button
              onClick={() => user ? navigate("/generate") : navigate("/signup")}
              className="h-12 px-8 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold hover:shadow-lg"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Start Creating Free
            </Button>
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-white rounded-3xl p-8 border border-slate-100 mb-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Frequently Asked Questions
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-slate-100 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="font-medium text-slate-800">{faq.q}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-violet-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-4">
                    <p className="text-slate-600">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-center gap-8 text-slate-400">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            <span className="text-sm">Secure Payments via Stripe</span>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5" />
            <span className="text-sm">7-Day Money-Back Guarantee</span>
          </div>
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            <span className="text-sm">Cancel Anytime</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Pricing;
